import React, { useState, useEffect, useContext } from 'react';
import { profilesApi } from '../../api/profiles';
import { UserContext } from '../../context/UserContext';
import HrnbModal from '../controls/HrnbModal';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbInput from '../controls/HrnbInput';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import { chatsApi, ChatType } from '../../api/chats';
import '../../css/chats/NewChatModal.css';

/**
 * Модальное окно создания нового чата
 * Поддерживает DIRECT (1×1, без заголовка) и GROUP (с заголовком и несколькими участниками)
 */
export default function NewChatModal({ onClose, onChatCreated }) {
    const { currentUser } = useContext(UserContext);
    const [profiles, setProfiles] = useState([]);
    const [chatType, setChatType] = useState(ChatType.DIRECT);
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [selectedGroupProfileIds, setSelectedGroupProfileIds] = useState([]);
    const [chatTitle, setChatTitle] = useState('');
    const [loadingProfiles, setLoadingProfiles] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Универсальный ID профиля текущего пользователя
    const currentProfileId = currentUser?.profileId || currentUser?.profiles?.[0]?.id;

    const fetchProfiles = React.useCallback(async () => {
        setLoadingProfiles(true);
        try {
            const response = await profilesApi.list({ size: 1000 });
            // API v2 возвращает { content: [...], page: {...} }
            const profilesList = Array.isArray(response?.content)
                ? response.content
                : Array.isArray(response) ? response : [];
            // Исключаем профиль текущего пользователя из списка
            const filteredProfiles = profilesList.filter(p => p.id !== currentProfileId);
            setProfiles(filteredProfiles);
        } catch (err) {
            console.error('Failed to fetch profiles:', err);
            setError('Не удалось загрузить список пользователей');
        } finally {
            setLoadingProfiles(false);
        }
    }, [currentProfileId]);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    // Проверка загрузки currentUser — после всех хуков
    if (!currentUser || !currentProfileId) {
        return (
            <HrnbModal open={true} onClose={onClose} title="Создать новый чат">
                <div className="new-chat-modal__loading">
                    <HrnbLoader />
                </div>
            </HrnbModal>
        );
    }

    const toggleGroupParticipant = (profileId) => {
        setSelectedGroupProfileIds(prev =>
            prev.includes(profileId)
                ? prev.filter(id => id !== profileId)
                : [...prev, profileId]
        );
        setError(null);
    };

    const handleCreateChat = async () => {
        setError(null);

        if (chatType === ChatType.DIRECT) {
            if (!selectedProfileId) {
                setError('Выберите пользователя для чата');
                return;
            }
        } else {
            if (!chatTitle.trim()) {
                setError('Введите название группового чата');
                return;
            }
            if (selectedGroupProfileIds.length === 0) {
                setError('Выберите хотя бы одного участника');
                return;
            }
        }

        setLoading(true);

        try {
            let createdChat;
            if (chatType === ChatType.DIRECT) {
                createdChat = await chatsApi.createChat({
                    participants: [currentProfileId, selectedProfileId],
                    admins: [currentProfileId],
                    chatType: ChatType.DIRECT,
                    isArchived: false
                });
            } else {
                createdChat = await chatsApi.createChat({
                    title: chatTitle.trim(),
                    participants: [currentProfileId, ...selectedGroupProfileIds],
                    admins: [currentProfileId],
                    chatType: ChatType.GROUP,
                    isArchived: false
                });
            }
            onChatCreated(createdChat);
            onClose();
        } catch (err) {
            setError(err.message || 'Ошибка при создании чата');
        } finally {
            setLoading(false);
        }
    };

    // Формируем опции для HrnbSelect из публичных профилей
    const profileOptions = profiles.map(profile => ({
        value: profile.id,
        label: profile.displayName || 'Пользователь'
    }));

    return (
        <HrnbModal
            open={true}
            onClose={onClose}
            title="Создать новый чат"
        >
            <div className="new-chat-modal">
                {/* Выбор типа чата */}
                <div className="new-chat-modal__section">
                    <label className="new-chat-modal__label">Тип чата:</label>
                    <div className="new-chat-modal__type-toggle">
                        <label className={`new-chat-modal__type-option ${chatType === ChatType.DIRECT ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="chatType"
                                value={ChatType.DIRECT}
                                checked={chatType === ChatType.DIRECT}
                                onChange={() => {
                                    setChatType(ChatType.DIRECT);
                                    setError(null);
                                }}
                            />
                            Личное сообщение
                        </label>
                        <label className={`new-chat-modal__type-option ${chatType === ChatType.GROUP ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="chatType"
                                value={ChatType.GROUP}
                                checked={chatType === ChatType.GROUP}
                                onChange={() => {
                                    setChatType(ChatType.GROUP);
                                    setError(null);
                                }}
                            />
                            Групповой чат
                        </label>
                    </div>
                </div>

                {loadingProfiles ? (
                    <div className="new-chat-modal__loading">
                        <HrnbLoader />
                    </div>
                ) : (
                    <>
                        {chatType === ChatType.GROUP && (
                            <div className="new-chat-modal__section">
                                <HrnbInput
                                    label="Название чата"
                                    value={chatTitle}
                                    onChange={(e) => setChatTitle(e.target.value)}
                                    placeholder="Введите название группы..."
                                />
                            </div>
                        )}

                        <div className="new-chat-modal__section">
                            {chatType === ChatType.DIRECT ? (
                                <HrnbSelect
                                    label="Выберите пользователя:"
                                    options={profileOptions}
                                    value={selectedProfileId}
                                    onChange={(value) => {
                                        setSelectedProfileId(value);
                                        setError(null);
                                    }}
                                    placeholder="Выберите пользователя..."
                                    className="new-chat-modal__select"
                                />
                            ) : (
                                <>
                                    <label className="new-chat-modal__label">Участники:</label>
                                    <div className="new-chat-modal__users-list">
                                        {profiles.length === 0 ? (
                                            <div className="new-chat-modal__no-users">Нет доступных пользователей</div>
                                        ) : (
                                            profiles.map(profile => (
                                                <div
                                                    key={profile.id}
                                                    className={`new-chat-modal__user-item ${selectedGroupProfileIds.includes(profile.id) ? 'selected' : ''}`}
                                                    onClick={() => toggleGroupParticipant(profile.id)}
                                                >
                                                    <span className="new-chat-modal__user-name">
                                                        {profile.displayName || 'Пользователь'}
                                                    </span>
                                                    {selectedGroupProfileIds.includes(profile.id) && (
                                                        <span className="new-chat-modal__check">✓</span>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    {selectedGroupProfileIds.length > 0 && (
                                        <div className="new-chat-modal__selected-count">
                                            Выбрано: {selectedGroupProfileIds.length}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {error && (
                    <HrnbAlert variant="danger">{error}</HrnbAlert>
                )}

                <div className="new-chat-modal__actions">
                    <HrnbButton variant="secondary" onClick={onClose}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton
                        onClick={handleCreateChat}
                        disabled={
                            loading ||
                            loadingProfiles ||
                            (chatType === ChatType.DIRECT ? !selectedProfileId : (!chatTitle.trim() || selectedGroupProfileIds.length === 0))
                        }
                    >
                        {loading ? <HrnbLoader size="sm" /> : 'Создать чат'}
                    </HrnbButton>
                </div>
            </div>
        </HrnbModal>
    );
}
